import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["offset", "children"];
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { viewports } from '../helper';
import config, { renderMediaQueries } from './config';
import ScopedCssBaseline from '../StyleBase/ScopedCssBaseline';
const PROPS_TO_VALIDATE = ['smallMobile', 'mobile', 'tablet', 'desktop', 'largeDesktop'];
const MIN_COLUMNS = 1;
const MAX_COLUMNS = 12;

const resolveGutter = () => renderMediaQueries(dimension => {
  const gutterWidth = parseFloat(config.gutterWidth[dimension]) / 2;
  return css(["padding-right:", "px;padding-left:", "px;"], gutterWidth, gutterWidth);
});

const resolveOffset = offset => renderMediaQueries(dimension => {
  if (typeof offset === 'number') {
    return css(["margin-left:", "%;"], offset / config.columns[dimension] * 100);
  }

  const size = offset[dimension] === undefined || offset[dimension] < 0 ? 0 : offset[dimension];
  const margin = size / config.columns[dimension] * 100;
  return css(["margin-left:", "%;"], margin);
});

const resolveGrid = props => {
  let nextColumnNumber;
  return renderMediaQueries(dimension => {
    const columnNumber = props[dimension] || nextColumnNumber;

    if (!columnNumber) {
      return css(["flex-basis:auto;max-width:none;"]);
    }

    const percent = parseFloat(columnNumber / config.columns[dimension]) * 100;
    nextColumnNumber = columnNumber;
    return css(["flex:1 1 ", "%;max-width:", "%;"], percent, percent);
  });
};

const Root = styled.div.withConfig({
  displayName: "GridColumn__Root",
  componentId: "sc-1mru928-0"
})(["", " flex:1 0 auto;width:100%;", " ", " ", ""], ScopedCssBaseline, props => resolveGrid(props), _ref => {
  let {
    withoutGutter
  } = _ref;
  return !withoutGutter && resolveGutter();
}, _ref2 => {
  let {
    sizeOffset
  } = _ref2;
  return sizeOffset && resolveOffset(sizeOffset);
});

function validateMinMaxNumberProps(props) {
  Object.entries(props).forEach(_ref3 => {
    let [propName, propValue] = _ref3;
    if (!PROPS_TO_VALIDATE.includes(propName)) return;
    if (propValue == null) return;

    if (propValue < MIN_COLUMNS || propValue > MAX_COLUMNS) {
      return console.warn("Tangram: The prop ".concat(propName, " must be a number between ").concat(MIN_COLUMNS, " and ").concat(MAX_COLUMNS, ", but your value is ").concat(propValue, "."));
    }
  });
}

const GridColumn = /*#__PURE__*/forwardRef((_ref4, ref) => {
  let {
    offset,
    children
  } = _ref4,
      rest = _objectWithoutPropertiesLoose(_ref4, _excluded);

  validateMinMaxNumberProps(rest);
  return /*#__PURE__*/React.createElement(Root, _extends({}, rest, {
    ref: ref,
    sizeOffset: offset
  }), children);
});
GridColumn.viewports = {
  smallMobile: viewports.smallMobile,
  mobile: viewports.mobile,
  tablet: viewports.tablet,
  desktop: viewports.desktop,
  largeDesktop: viewports.largeDesktop
};
GridColumn.defaultProps = {
  smallMobile: null,
  mobile: null,
  tablet: null,
  desktop: null,
  largeDesktop: null,
  offset: {},
  withoutGutter: false,
  children: null
};
process.env.NODE_ENV !== "production" ? GridColumn.propTypes = {
  /**
   * Sets the number of columns on screens small mobile.
   *
   * Available values are range from 1 to 12.
   */
  smallMobile: PropTypes.number,

  /**
   * Sets the number of columns on screens mobile.
   *
   * Available values are range from 1 to 12.
   */
  mobile: PropTypes.number,

  /**
   * Sets the number of columns on screens tablet.
   *
   * Available values are range from 1 to 12.
   */
  tablet: PropTypes.number,

  /**
   * Sets the number of columns on screens desktop.
   *
   * Available values are range from 1 to 12.
   */
  desktop: PropTypes.number,

  /**
   * Sets the number of columns on screens large desktop.
   *
   * Available values are range from 1 to 12.
   */
  largeDesktop: PropTypes.number,

  /**
   * Sets the number of the offset columns.
   */
  offset: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),

  /**
   * Removes the gutter.
   */
  withoutGutter: PropTypes.bool,

  /**
   * Sets children.
   */
  children: PropTypes.node
} : void 0;
export default GridColumn;